/**
 * Onde ficam o Keycloak e a API, e com que identidade esta aplicacao se
 * apresenta.
 *
 * Tudo vem de variavel VITE_*, que entra no pacote publicado — por isso nada
 * aqui e segredo. O cliente `teg-web` e publico de proposito: aplicacao de
 * pagina unica nao guarda segredo (docs/AMBIENTES-E-CICD.md, secao 5).
 */

/**
 * Nivel de autenticacao que esta aplicacao exige do Keycloak.
 *
 * O realm mapeia `acr.loa.map = {"1":1,"2":2}`: nivel 1 e senha, nivel 2 e
 * senha mais segundo fator. Pedir `acr_values=2` no fluxo de autorizacao faz o
 * subfluxo condicional de OTP entrar em cena, e faz o token sair com `acr=2` —
 * que e o que a API cobra de papel interno (ACR_MINIMO_INTERNO).
 *
 * O cliente `teg-web` tambem carrega `default.acr.values` no realm, entao a
 * exigencia vale mesmo que esta aplicacao esqueca o parametro. As duas pontas
 * de proposito: aqui fica visivel no codigo, la fica valendo para qualquer
 * chamador.
 */
export const NIVEL_SEGUNDO_FATOR = '2';

/** Escopos pedidos. `openid` e obrigatorio para receber o token de identidade. */
export const ESCOPOS = 'openid profile email';

/** Publico: PrimeiroAcessoPage le variavel avulsa, fora de ConfiguracaoAuth. */
export function exigir(nome: string, valor: string | undefined): string {
  if (!valor || valor.trim().length === 0) {
    // Falta de configuracao e defeito de implantacao, e aparece no arranque —
    // nao na tela do usuario no meio de uma entrada.
    throw new Error(`Variavel de ambiente ${nome} nao configurada.`);
  }
  return valor.trim().replace(/\/+$/, '');
}

export interface ConfiguracaoAuth {
  /** Endereco base da API do TEG. */
  api: string;
  /** Emissor do realm, base de todos os enderecos do protocolo. */
  emissor: string;
  clienteId: string;
  autorizacao: string;
  token: string;
  saida: string;
  /** Para onde o Keycloak devolve o navegador depois da autorizacao. */
  retorno: string;
  ambiente: string;
}

/**
 * Monta a configuracao. Os caminhos do protocolo sao os do Keycloak e nao
 * mudam entre versoes; buscar o documento de descoberta custaria uma ida a
 * rede antes de qualquer entrada, e deixaria o teste dependente de rede.
 */
export function lerConfiguracao(
  ambienteVite: Record<string, string | undefined> = import.meta.env as unknown as Record<
    string,
    string | undefined
  >,
  origem: string = typeof window === 'undefined' ? '' : window.location.origin,
): ConfiguracaoAuth {
  const keycloak = exigir('VITE_KEYCLOAK_URL', ambienteVite.VITE_KEYCLOAK_URL);
  const realm = exigir('VITE_KEYCLOAK_REALM', ambienteVite.VITE_KEYCLOAK_REALM);
  const emissor = `${keycloak}/realms/${realm}`;
  const protocolo = `${emissor}/protocol/openid-connect`;

  return {
    api: exigir('VITE_API_URL', ambienteVite.VITE_API_URL),
    emissor,
    clienteId: exigir('VITE_KEYCLOAK_CLIENT_ID', ambienteVite.VITE_KEYCLOAK_CLIENT_ID),
    autorizacao: `${protocolo}/auth`,
    token: `${protocolo}/token`,
    saida: `${protocolo}/logout`,
    // A volta e a propria tela de entrada: ela reconhece o codigo na URL e
    // conclui. Rota tecnica separada seria rota sem tela (CLAUDE.md, "Proibido").
    retorno: `${origem}${import.meta.env.BASE_URL}login`,
    ambiente: ambienteVite.VITE_AMBIENTE ?? 'DSV',
  };
}
